// Patients roster — a clean table (replaces the old master-detail combo). Demographics only via the
// assigned-roster endpoint (RLS); clicking a row opens the patient cockpit. No clinical data here.
import "../styles-patients.css";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { age } from "../lib/format";
import { Loading, ErrorState } from "../components/ui";
import type { RosterPatient } from "@forcebase-ai/schema/doctor";

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

type Filter = "all" | "seen" | "new";

const SearchIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const ChevronIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
);

const initials = (p: RosterPatient) => `${p.first_name.charAt(0)}${p.last_name.charAt(0)}`.toUpperCase();

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "seen", label: "Seen" },
  { key: "new", label: "New" },
];

export function Patients() {
  const api = useApi();
  const navigate = useNavigate();
  const { data, loading, error } = useAsync(() => api.patients(), []);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} />;

  const roster = (data ?? [])
    // Search by name (the roster payload is demographics-only — no insurance/condition fields here).
    .filter((p) => `${p.first_name} ${p.last_name}`.toLowerCase().includes(q.toLowerCase()))
    .filter((p) =>
      filter === "all" ? true : filter === "new" ? !p.last_visit_at : Boolean(p.last_visit_at),
    );

  return (
    <>
      <div className="dp-head">
        <div>
          <div className="dp-head__greet">Patient management</div>
          <h1 className="dp-head__title">Your <em>patients.</em></h1>
        </div>
      </div>

      <div className="ptbl-toolbar">
        <div className="ptbl-search">
          {SearchIcon}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patients by name…" aria-label="Search patients" />
        </div>
        <div className="ptbl-chips">
          {FILTERS.map((f) => (
            <button key={f.key} className={`ptbl-chip ${filter === f.key ? "is-active" : ""}`} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="ptbl-wrap">
        <table className="ptbl">
          <thead>
            <tr>
              <th>Patient</th>
              <th>Last visit</th>
              <th className="ptbl-unread-col">Unread</th>
              <th aria-label="Open" />
            </tr>
          </thead>
          <tbody>
            {roster.length ? (
              roster.map((p) => (
                <tr
                  key={p.id}
                  className="ptbl-row"
                  onClick={() => navigate(`/patients/${p.id}`)}
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(`/patients/${p.id}`)}
                >
                  <td>
                    <div className="ptbl-patient">
                      <span className="ptbl-avatar">{initials(p)}</span>
                      <div>
                        <div className="ptbl-name">{p.first_name} {p.last_name}</div>
                        <div className="ptbl-sub">{age(p.dob)} yrs{p.dob ? ` · DOB ${p.dob}` : ""}</div>
                      </div>
                    </div>
                  </td>
                  <td className="ptbl-cell-muted">
                    {p.last_visit_at ? shortDate(p.last_visit_at) : <span className="ptbl-tag-new">New patient</span>}
                  </td>
                  <td className="ptbl-unread-col">
                    {/* Real unread count is a follow-up — placeholder for now. */}
                    <span className="ptbl-unread ptbl-unread--none">—</span>
                  </td>
                  <td className="ptbl-open">{ChevronIcon}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={4} className="ptbl-empty">No patients match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
